import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, CalendarRange, CalendarCheck, Minus, Plus } from 'lucide-react';
import { ATAJOS_DE_ZOOM } from './constants';
import { PX_POR_DIA_MAX, PX_POR_DIA_MIN, detalleDelEje } from './ganttEje';

//: Desde donde arranca el eje, contado hacia atras desde hoy. `null` = todo lo que haya.
//:
//: **El default es «todo», y es una decision medida, no pereza.** Ninguno de los 69 casos de
//: Pritty tiene plan propio, asi que la barra de cada uno sale de su fecha de creacion — y hay
//: nueve casos de 2016 a 2022. Con una ventana de un mes, **24 de 69 filas quedarian sin barra**:
//: el Gantt abriria limpio y mostrando menos trabajo del que hay. Lo que resolvia el problema
//: real —scrollear diez años hasta esta semana— es posicionarse en hoy, que ahora pasa solo.
export const VENTANAS = [
  { id: 'trimestre', label: '3 meses', dias: 92 },
  { id: 'anio', label: '1 año', dias: 365 },
  { id: 'todo', label: 'Todo', dias: null },
];

export default function GanttToolbar({
  meta, filters,
  pxPorDia, onZoom,
  showAssignees, onShowAssigneesChange,
  verCarga, onVerCargaChange,
  guiaAbierta, onGuiaToggle,
  ventana, onVentanaChange,
  onIrAHoy, fueraDeRango = 0,
}) {
  const qs = new URLSearchParams();
  Object.entries(filters || {}).forEach(([k, v]) => { if (v) qs.set(k, v); });
  const execUrl = `/executive${qs.toString() ? `?${qs}` : ''}`;
  const detalle = detalleDelEje(pxPorDia);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border border-brand-border rounded-sm p-2 bg-white text-xs" data-testid="gantt-toolbar">
      <div className="flex items-center gap-2">
        <Link to={execUrl}>
          <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm">
            <ArrowLeft className="w-3 h-3 mr-1" /> Ejecutivo
          </Button>
        </Link>
        <Button
          variant="outline" size="sm" className="h-7 text-[10px] rounded-sm"
          onClick={onIrAHoy} data-testid="gantt-ir-a-hoy"
          title="Volver a la fecha de hoy"
        >
          <CalendarCheck className="w-3 h-3 mr-1" /> Hoy
        </Button>
        <span className="text-[10px] text-zinc-500 flex items-center gap-1">
          <CalendarRange className="w-3 h-3" />
          {meta?.range_start} — {meta?.range_end}
        </span>
        {/* Desde donde arranca el eje. El rango **no** oculta filas: la WBS sigue trayendo todo,
            y lo que queda afuera se marca en su fila — por eso al lado va el contador, que es lo
            que convierte «no veo la barra» en «ensanchá el rango». */}
        <select
          value={ventana || 'todo'}
          onChange={(e) => onVentanaChange?.(e.target.value)}
          className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
          data-testid="gantt-ventana"
          aria-label="Desde cuándo"
        >
          {VENTANAS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
        {fueraDeRango > 0 && (
          <span
            className="text-[9px] uppercase tracking-wider px-1 py-px border border-amber-300 bg-[#FFFCF2] text-[#8A6400] rounded-sm"
            title="Esas filas estan en la lista pero su barra cae fuera del rango. Ensanchalo para verlas."
            data-testid="gantt-fuera-de-rango-total"
          >
            {fueraDeRango} fuera del rango
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-[10px] text-zinc-600 cursor-pointer select-none">
          <input
            type="checkbox" checked={showAssignees}
            onChange={(e) => onShowAssigneesChange?.(e.target.checked)}
            data-testid="gantt-toggle-assignees" className="rounded border-brand-border"
          />
          Responsables
        </label>
        <label className="flex items-center gap-1.5 text-[10px] text-zinc-600 cursor-pointer select-none">
          <input
            type="checkbox" checked={verCarga}
            onChange={(e) => onVerCargaChange?.(e.target.checked)}
            data-testid="gantt-toggle-carga" className="rounded border-brand-border"
          />
          Carga
        </label>

        {/* Zoom continuo. Los tres botones de antes quedan como atajos, no como modos: la
            escala util casi nunca es una de tres, y saltar entre ellas pierde el lugar. */}
        <div className="flex items-center gap-1 border-l border-brand-border pl-2">
          {ATAJOS_DE_ZOOM.map((a) => (
            <Button
              key={a.id}
              size="sm"
              variant={detalle === a.id ? 'default' : 'outline'}
              className="h-7 text-[10px] rounded-sm px-2"
              onClick={() => onZoom(a.px)}
              data-testid={`gantt-scale-${a.id}`}
            >
              {a.label}
            </Button>
          ))}
          <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" onClick={() => onZoom(pxPorDia / 1.3)} aria-label="Alejar">
            <Minus className="w-3 h-3" />
          </Button>
          <input
            type="range" min={PX_POR_DIA_MIN} max={PX_POR_DIA_MAX} step="1" value={pxPorDia}
            onChange={(e) => onZoom(Number(e.target.value))}
            className="w-24 accent-[#002FA7]"
            aria-label="Zoom del eje"
            data-testid="gantt-zoom"
          />
          <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" onClick={() => onZoom(pxPorDia * 1.3)} aria-label="Acercar">
            <Plus className="w-3 h-3" />
          </Button>
          <span className="text-[9px] text-zinc-400 font-mono w-14 text-right hidden sm:inline" title="Con ⌘/Ctrl + rueda, el zoom ancla en el puntero">
            {Math.round(pxPorDia)} px/día
          </span>
        </div>

        <Button
          size="sm"
          variant={guiaAbierta ? 'default' : 'outline'}
          className="h-7 text-[10px] rounded-sm px-2"
          onClick={onGuiaToggle}
          data-testid="gantt-guia-toggle"
        >
          <BookOpen className="w-3 h-3 mr-1" /> Cómo se lee
        </Button>
      </div>
    </div>
  );
}
